import { useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';

/**
 * Global Content Protection Component
 * Applies screenshot/copy protection across the entire app for non-admin users
 * Tracks violations and stores them for admin review
 */
const GlobalContentProtection = () => {
    const { user, api } = useAuth();
    const violationCountRef = useRef(0);
    const lastReportedRef = useRef(null);
    
    // Don't apply protection for admins
    const isAdmin = user?.role === 'admin' || user?.feature_access?.quality_admin === true;
    
    const reportViolation = useCallback(async (type, details = {}) => {
        if (isAdmin) return;
        
        violationCountRef.current += 1;
        const violation = {
            type,
            timestamp: new Date().toISOString(),
            count: violationCountRef.current,
            userId: user?.id,
            userName: user?.name || user?.email,
            userAgent: navigator.userAgent,
            page: window.location.pathname,
            ...details
        };
        
        // Store locally
        const stored = JSON.parse(localStorage.getItem('global_violations') || '[]');
        stored.push(violation);
        // Keep only last 200 violations locally
        if (stored.length > 200) stored.shift();
        localStorage.setItem('global_violations', JSON.stringify(stored));
        
        // Report to backend (debounced - don't spam)
        const now = Date.now();
        if (!lastReportedRef.current || now - lastReportedRef.current > 5000) {
            lastReportedRef.current = now;
            try {
                await api.post('/content-protection/report-violation', violation);
            } catch (err) {
                // Silently fail - don't disrupt user experience
                console.debug('Failed to report violation:', err);
            }
        }
        
        return violation;
    }, [isAdmin, user, api]);

    useEffect(() => {
        // Skip for admins
        if (isAdmin) return;

        // Prevent keyboard shortcuts for screenshots and copying
        const handleKeyDown = (e) => {
            // Screenshot keys are handled by the Focus Guard screenshot guard (blur + sign-out)
            // Prevent Ctrl+P (Print)
            if (e.ctrlKey && e.key === 'p') {
                e.preventDefault();
                reportViolation('print_attempt', { key: 'Ctrl+P' });
                return false;
            }

            // Prevent Ctrl+Shift+I (DevTools) - but allow in development
            if (e.ctrlKey && e.shiftKey && e.key === 'I' && process.env.NODE_ENV === 'production') {
                e.preventDefault();
                reportViolation('devtools_attempt', { key: 'Ctrl+Shift+I' });
                return false;
            }

            // Prevent F12 (DevTools) in production
            if (e.key === 'F12' && process.env.NODE_ENV === 'production') {
                e.preventDefault();
                reportViolation('devtools_f12', { key: 'F12' });
                return false;
            }
        };

        // Detect visibility change (tab switch - possible screenshot via screen recording)
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'hidden') {
                reportViolation('tab_hidden', { reason: 'Tab switched or minimized' });
            }
        };
        
        // Detect window blur (possible snipping tool activation)
        let blurTimeout = null;
        const handleBlur = () => {
            // Clear any existing timeout
            if (blurTimeout) clearTimeout(blurTimeout);
            
            // Wait a moment before recording - sometimes focus comes right back
            blurTimeout = setTimeout(() => {
                if (!document.hasFocus()) {
                    reportViolation('window_blur', { reason: 'Window lost focus (possible snipping tool)' });
                }
            }, 1500); // 1.5 seconds delay to avoid false positives
        };

        // Add event listeners
        document.addEventListener('keydown', handleKeyDown);
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('blur', handleBlur);

        // Cleanup on unmount
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('blur', handleBlur);
            if (blurTimeout) clearTimeout(blurTimeout);
        };
    }, [isAdmin, reportViolation]);
    
    // This component doesn't render anything visible
    return null;
};

export default GlobalContentProtection;
